import { api } from './client';
import { useAuthStore } from '@/store/auth';

export interface UserOut {
  id: string;
  email: string;
  phone: string | null;
  cpf: string | null;
  name: string;
  active: boolean;
  profile_complete: boolean;
  /** Troca de email aguardando confirmação no email novo (null = nenhuma). */
  pending_email?: string | null;
  /** false = conta só-Google/Apple (sem senha local). Opcional: o store
   *  persistido pode ter objetos antigos sem o campo. */
  has_password?: boolean;
  /** Provedores sociais vinculados — decide o botão de re-auth na troca de email. */
  has_google?: boolean;
  has_apple?: boolean;
  created_at: string;
}

export interface TokenOut {
  access_token: string;
  token_type: 'bearer';
  expires_in: number;
  profile_complete: boolean;
}

export interface RegisterIn {
  email: string;
  phone: string;
  cpf: string;
  name: string;
  password: string;
  consent_terms: true;
  consent_privacy: true;
  consent_marketing?: boolean;
}

export interface CompleteProfileIn {
  phone: string;
  cpf: string;
  consent_terms: true;
  consent_privacy: true;
  consent_marketing?: boolean;
}

export interface UpdateProfileIn {
  name?: string;
  phone?: string;
  consent_marketing?: boolean;
}

export interface ChangePasswordIn {
  current_password: string;
  new_password: string;
}

export interface MeStatsOut {
  kwh_total: string;
  cashback_total_brl: string;
  co2_kg_avoided: string;
  sessions_count: number;
}

export interface ForgotPasswordIn {
  email: string;
}

export interface ForgotPasswordOut {
  detail: string;
}

export interface ResetPasswordIn {
  token: string;
  new_password: string;
}

export interface ResetPasswordOut {
  detail: string;
}

export const authApi = {
  register: (body: RegisterIn) => api.post<UserOut>('/auth/register', body),
  login: (email: string, password: string) =>
    api.post<TokenOut>('/auth/login', { email, password }),
  google: (idToken: string) =>
    api.post<TokenOut>('/auth/google', { id_token: idToken }),
  apple: (idToken: string, name?: string | null) =>
    api.post<TokenOut>('/auth/apple', { id_token: idToken, name: name ?? null }),
  completeProfile: (body: CompleteProfileIn) =>
    api.post<UserOut>('/auth/complete-profile', body),
  updateProfile: (body: UpdateProfileIn) =>
    api.patch<UserOut>('/auth/me', body),
  deleteAccount: () => api.del<void>('/auth/me'),
  me: () => api.get<UserOut>('/auth/me'),
  refresh: () => api.post<TokenOut>('/auth/refresh'),
  meStats: () => api.get<MeStatsOut>('/auth/me/stats'),
  changePassword: (body: ChangePasswordIn) =>
    api.post<void>('/auth/me/password', body),
  forgotPassword: (email: string) =>
    api.post<ForgotPasswordOut>('/auth/forgot-password', { email }),
  resetPassword: (token: string, newPassword: string) =>
    api.post<ResetPasswordOut>('/auth/reset-password', { token, new_password: newPassword }),
  /** Inicia troca de email — link de confirmação vai pro email NOVO.
   *  Segundo fator: senha (conta com senha) OU id_token fresco do
   *  Google/Apple (conta só-social, reautenticada na hora). */
  requestEmailChange: (
    newEmail: string,
    factor: {
      currentPassword?: string;
      googleIdToken?: string;
      appleIdToken?: string;
    } = {}
  ) =>
    api.post<{ detail: string }>('/auth/me/email', {
      new_email: newEmail,
      current_password: factor.currentPassword ?? null,
      google_id_token: factor.googleIdToken ?? null,
      apple_id_token: factor.appleIdToken ?? null,
    }),
  /** Aplica a troca (token do link). Revoga todas as sessões — relogin. */
  confirmEmail: (token: string) =>
    api.post<{ detail: string }>('/auth/confirm-email', { token }),
};

/**
 * Lê o claim `exp` (epoch em segundos) do payload do JWT, sem verificar
 * assinatura — só pra decidir client-side se vale renovar. null se inválido.
 */
export function getTokenExp(token: string): number | null {
  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64));
    return typeof payload.exp === 'number' ? payload.exp : null;
  } catch {
    return null;
  }
}

/** Renova quando falta menos da metade da janela de 30 dias. */
const REFRESH_WINDOW_S = 15 * 24 * 3600;

/**
 * Sessão deslizante: se o token do store está na metade final da validade,
 * troca por um novo via POST /auth/refresh. Silencioso — falha (rede, 401)
 * mantém o token atual e deixa o fluxo normal tratar. Token expirado de
 * verdade → 401 → logout pelo interceptor do client.ts (comportamento certo).
 */
export async function maybeRefreshToken(): Promise<void> {
  const token = useAuthStore.getState().token;
  if (!token) return;
  const exp = getTokenExp(token);
  if (exp === null) return;
  if (exp - Date.now() / 1000 > REFRESH_WINDOW_S) return; // ainda fresco
  try {
    const fresh = await authApi.refresh();
    useAuthStore.setState({ token: fresh.access_token });
  } catch {
    /* silencioso — ver docstring */
  }
}

/**
 * Autentica via email/senha e carrega user. Backend separa /login (token) de
 * /me (user), então chamamos ambos em sequência.
 */
export async function signIn(email: string, password: string): Promise<UserOut> {
  const token = await authApi.login(email, password);
  useAuthStore.setState({ token: token.access_token });
  try {
    const user = await authApi.me();
    useAuthStore.getState().login(token.access_token, user);
    return user;
  } catch (err) {
    useAuthStore.setState({ token: null, user: null });
    throw err;
  }
}

/** Autentica via Google ID token. Mesmo padrão do signIn. */
export async function signInWithGoogle(idToken: string): Promise<UserOut> {
  const token = await authApi.google(idToken);
  useAuthStore.setState({ token: token.access_token });
  try {
    const user = await authApi.me();
    useAuthStore.getState().login(token.access_token, user);
    return user;
  } catch (err) {
    useAuthStore.setState({ token: null, user: null });
    throw err;
  }
}

/** Autentica via Apple ID token (Sign in with Apple). Mesmo padrão do Google. */
export async function signInWithApple(
  idToken: string,
  name?: string | null
): Promise<UserOut> {
  const token = await authApi.apple(idToken, name);
  useAuthStore.setState({ token: token.access_token });
  try {
    const user = await authApi.me();
    useAuthStore.getState().login(token.access_token, user);
    return user;
  } catch (err) {
    useAuthStore.setState({ token: null, user: null });
    throw err;
  }
}
